import i18n from '@/utils/i18n';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import {
  ESTIMATED_HEADER_HEIGHT,
  MIN_NAME_COLUMN_SIZE,
  NAME_COLUMN_PERCENTAGE,
  ROW_HEIGHT,
} from '@/experiences/sales-portal/domain/prospects/screens/prospects/components/prospects-manager-view/manager-table-constants';
import { Skeleton } from '@usepepper/sprout-kit-ui';
import { View, ViewStyle } from 'react-native';

const AVATAR_SIZE = 32;
const CELL_TEXT_HEIGHT = 14;
const CELL_TEXT_WIDTH = 80;
const HEADER_TEXT_HEIGHT = 12;
const HEADER_TEXT_WIDTH = 72;
const NAME_CELL_HEIGHT = 14;
const NAME_CELL_WIDTH = 180;
const SKELETON_ROW_COUNT = 8;

const getRowFadeStyle = (index: number): ViewStyle => ({
  opacity: 1 - index * 0.1,
});

interface ManagerTableSkeletonProps {
  columnCount: number;
  columnPercentage: number;
  minColumnWidth: number;
}

export const ManagerTableSkeleton: React.FC<ManagerTableSkeletonProps> = ({
  columnCount,
  columnPercentage,
  minColumnWidth,
}) => {
  const [styles, theme] = useStyles();

  const columnStyle: ViewStyle = {
    flex: columnPercentage,
    minWidth: minColumnWidth,
  };

  return (
    <View
      accessibilityLabel={i18n.t('sales_portal.prospects.loading')}
      accessibilityRole="progressbar"
      accessibilityState={{ busy: true }}
    >
      <View style={styles.headerRow}>
        <View style={[styles.headerCell, styles.nameColumn]}>
          <Skeleton
            borderRadius={theme.sk.radii.radiusMedium}
            height={HEADER_TEXT_HEIGHT}
            loadingLabel=""
            width={HEADER_TEXT_WIDTH}
          />
        </View>
        {Array.from({ length: columnCount }).map((_, columnIndex) => (
          <View key={columnIndex} style={[styles.headerCell, columnStyle]}>
            <Skeleton
              borderRadius={theme.sk.radii.radiusMedium}
              height={HEADER_TEXT_HEIGHT}
              loadingLabel=""
              width={HEADER_TEXT_WIDTH}
            />
          </View>
        ))}
      </View>

      {Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIndex) => (
        <View key={rowIndex} style={getRowFadeStyle(rowIndex)}>
          <View style={styles.bodyRow}>
            <View style={[styles.bodyCell, styles.nameColumn]}>
              <View style={styles.nameCell}>
                <Skeleton
                  borderRadius={AVATAR_SIZE / 2}
                  height={AVATAR_SIZE}
                  loadingLabel=""
                  width={AVATAR_SIZE}
                />
                <Skeleton
                  borderRadius={theme.sk.radii.radiusMedium}
                  height={NAME_CELL_HEIGHT}
                  loadingLabel=""
                  width={NAME_CELL_WIDTH}
                />
              </View>
            </View>
            {Array.from({ length: columnCount }).map((_, columnIndex) => (
              <View key={columnIndex} style={[styles.bodyCell, columnStyle]}>
                <Skeleton
                  borderRadius={theme.sk.radii.radiusMedium}
                  height={CELL_TEXT_HEIGHT}
                  loadingLabel=""
                  width={CELL_TEXT_WIDTH}
                />
              </View>
            ))}
          </View>
          <View style={styles.divider} />
        </View>
      ))}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  bodyCell: {
    justifyContent: 'center',
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  bodyRow: {
    flexDirection: 'row',
    minHeight: ROW_HEIGHT,
  },
  divider: {
    backgroundColor: theme.sk.color.divider.default,
    height: 1,
  },
  headerCell: {
    justifyContent: 'center',
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  headerRow: {
    borderBottomWidth: 1,
    borderColor: theme.sk.color.border.primary,
    borderTopWidth: 1,
    flexDirection: 'row',
    minHeight: ESTIMATED_HEADER_HEIGHT,
  },
  nameCell: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceMedium,
  },
  nameColumn: {
    flex: NAME_COLUMN_PERCENTAGE,
    minWidth: MIN_NAME_COLUMN_SIZE,
  },
}));
